export function MySQLScriptsPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Available Scripts - MySQL Template</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          npm commands for development, migration management, and production.
        </p>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Development Scripts</h2>
        <div className="space-y-4">
          <ScriptCard
            command="npm run start:dev"
            description="Start development server with hot reload"
          />
          <ScriptCard
            command="npm run start:debug"
            description="Start development server with debugging enabled"
          />
          <ScriptCard
            command="npm run build"
            description="Build the application for production"
          />
          <ScriptCard
            command="npm run start:prod"
            description="Start production server (requires build first)"
          />
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Migration Scripts</h2>
        <div className="space-y-4">
          <ScriptCard
            command="npm run migration:run"
            description="Execute all pending migrations"
          />
          <ScriptCard
            command="npm run migration:revert"
            description="Revert the most recent migration"
          />
          <ScriptCard
            command="npm run migration:generate -- -n MigrationName"
            description="Generate a new migration from entity changes"
          />
          <ScriptCard
            command="npm run migration:create -- -n MigrationName"
            description="Create an empty migration file"
          />
          <ScriptCard
            command="npm run migration:show"
            description="Display all migrations and their status"
          />
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Code Quality Scripts</h2>
        <div className="space-y-4">
          <ScriptCard
            command="npm run lint"
            description="Run ESLint to check code quality"
          />
          <ScriptCard
            command="npm run format"
            description="Format code with Prettier"
          />
          <ScriptCard
            command="npm test"
            description="Run unit tests with Jest"
          />
          <ScriptCard
            command="npm run test:watch"
            description="Run tests in watch mode"
          />
          <ScriptCard
            command="npm run test:cov"
            description="Run tests with coverage report"
          />
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Helper Scripts</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Setup Script (Linux/Mac)</h3>
            <pre className="bg-muted p-3 rounded text-sm">
              <code>./scripts/setup.sh</code>
            </pre>
            <p className="text-sm text-muted-foreground">
              Interactive setup script that installs dependencies, configures environment variables, 
              and generates JWT secrets.
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Setup Script (Windows)</h3>
            <pre className="bg-muted p-3 rounded text-sm">
              <code>.\\scripts\\setup.bat</code>
            </pre>
            <p className="text-sm text-muted-foreground">
              Windows version of the setup script for automated project initialization.
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Migration Helper (Linux/Mac)</h3>
            <pre className="bg-muted p-3 rounded text-sm">
              <code>./scripts/migrations.sh</code>
            </pre>
            <p className="text-sm text-muted-foreground">
              Interactive script for common migration operations (run, revert, generate, create).
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">Migration Helper (Windows)</h3>
            <pre className="bg-muted p-3 rounded text-sm">
              <code>.\\scripts\\migrations.bat</code>
            </pre>
            <p className="text-sm text-muted-foreground">
              Windows version of the migration helper script.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-6 mt-8">
        <p className="text-sm font-semibold mb-2">💡 Tips</p>
        <ul className="space-y-2 text-sm">
          <li>• Use <code className="bg-muted px-2 py-1 rounded">npm run start:dev</code> for development with hot reload</li>
          <li>• Always run migrations before starting the app: <code className="bg-muted px-2 py-1 rounded">npm run migration:run</code></li>
          <li>• Generate migrations after changing entities: <code className="bg-muted px-2 py-1 rounded">npm run migration:generate</code></li>
          <li>• Test migrations in development before applying to production</li>
          <li>• Use <code className="bg-muted px-2 py-1 rounded">npm run test:cov</code> to ensure adequate test coverage</li>
        </ul>
      </div>
    </div>
  )
}

function ScriptCard({ command, description }: { command: string, description: string }) {
  return (
    <div className="bg-card border rounded-lg p-4 space-y-2">
      <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
        <code>{command}</code>
      </pre>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  )
}

